<script setup lang="ts">
  import { useAuthStore } from '@tundr/auth';
  import { PageSkeleton } from '@tundr/ds-components';
  import {
    LifecyclePhase,
    useLifecycleHooksStore,
  } from '@tundr/lifecycle-hooks';
  import { HR_THEME_CLASS } from '@tundr/hrTheme';
  import { HR_THEME_CLASS as DS_HR_THEME_CLASS } from '@tundr/ds-tokens-hr';
  import {
    computed,
    defineAsyncComponent,
    onMounted,
    onUnmounted,
    watch,
  } from 'vue';
  import { applicationWrapperClass, sessionInitLoaderClass } from './App.css';
  import GlobalElements from './GlobalElements.vue';
  import { ENV_VARS } from './core/config/env';
  import { useSupportChat } from './core/config/support/support';
  import { useTrackingSync } from './core/config/tracking/useTrackingSync';

  const authStore = useAuthStore();
  const lifecycleHooksStore = useLifecycleHooksStore();
  const { initSupportChat } = useSupportChat();

  useTrackingSync();

  const unsubscribe = authStore.$onAction(({ name, after }) => {
    if (name === 'setAuth') {
      after(() => {
        initSupportChat();
      });
    }
  });

  watch(
    () => authStore.selectedCompany?.id,
    () => {
      lifecycleHooksStore.reset(LifecyclePhase.SESSION_INIT);
    },
  );

  const isSessionInitRunning = computed(
    () =>
      lifecycleHooksStore.phaseStates.get(LifecyclePhase.SESSION_INIT)
        ?.status === 'running',
  );

  /**
   * Prevent middle mouse click default behavior
   */
  const MIDDLE_MOUSE_BUTTON = 1 as const;
  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button === MIDDLE_MOUSE_BUTTON) {
      e.preventDefault();
    }
  };

  onMounted(() => {
    document.body.classList.add(HR_THEME_CLASS);
    document.body.classList.add(DS_HR_THEME_CLASS);

    // Fix for middle mouse click on Windows
    document.addEventListener('pointerdown', onPointerDown, { capture: true });
  });

  onUnmounted(() => {
    document.removeEventListener('pointerdown', onPointerDown);

    // detach auth store subscription
    unsubscribe();
  });

  const DebugPanel =
    import.meta.env.VITE_ENVIRONMENT !== 'PRODUCTION'
      ? defineAsyncComponent(() =>
          import('@tundr/debug-panel').then(({ DebugPanel }) => DebugPanel),
        )
      : null;
</script>

<template>
  <GlobalElements />
  <DebugPanel
    v-if="DebugPanel"
    :environment="ENV_VARS.environment"
    :release="ENV_VARS.release"
    :api-base-url="ENV_VARS.api.baseUrl"
  />

  <div
    :class="applicationWrapperClass"
    :inert="isSessionInitRunning || undefined"
  >
    <router-view />
  </div>

  <div
    v-if="isSessionInitRunning"
    :class="sessionInitLoaderClass"
    data-testid="session-init-loader"
  >
    <PageSkeleton />
  </div>
</template>

<style>
  @import "@fontsource-variable/sofia-sans";
  @import "@fontsource-variable/inter";

  @import "@tundr/theme-assets/tundr-icons/tundr-icons.css";

  @import "vue-multiselect/dist/vue-multiselect.css";
</style>
